"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.RegisterForm = void 0;
var _zod = require("@hookform/resolvers/zod");
var _components = require("@ori-ui/components");
var _mui = require("@ori-ui/mui");
var _commonFormFields = require("@ori/common-form-fields");
var _testidGenerator = _interopRequireDefault(require("@ori/testid-generator"));
var _react = require("react");
var _reactHookForm = require("react-hook-form");
var _Localization = require("../Localization");
var _components2 = require("../components");
var _constants = require("../constants");
var _contexts = require("../contexts");
var _hooks = require("../hooks");
var _models = require("../models");
var _sections = require("../sections");
var _utils = require("../utils");
var _validationSchema = require("./validationSchema");
var _jsxRuntime = require("react/jsx-runtime");
const StyledForm = (0, _mui.styled)('form')(({
  theme
}) => ({
  maxWidth: '500px',
  marginInline: 'auto',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%'
  }
}));
const SubmitButtton = (0, _mui.styled)(_components.Button)(({
  theme
}) => ({
  minWidth: '330px',
  marginTop: theme.spacing(3),
  order: 100
}));
function checkNestedKey(obj, key) {
  if (!key || !obj) {
    return;
  }
  const keysArray = key.split('.');
  const currentKey = keysArray.shift();
  const exists = Object.keys(obj).find(objKey => objKey.toLowerCase() === currentKey?.toLowerCase());
  if (exists && keysArray.length) {
    // eslint-disable-next-line consistent-return --- we need as is
    return checkNestedKey(obj[exists], keysArray.join('.'));
  }

  // eslint-disable-next-line consistent-return --- we need as is
  return exists;
}
const RegisterForm = props => {
  const {
    config,
    initialData,
    awaitingRes,
    refreshMetadata,
    onSubmitForm,
    isParentalConsent
  } = props;
  const {
    formatMessage
  } = (0, _hooks.useTranslations)();
  const [focusSponsor, setFocusSponsor] = (0, _react.useState)(false);
  const {
    supportCenterUrl,
    customerReferral,
    loginUrl
  } = (0, _contexts.useConfiguration)();
  const {
    getTestIdProps
  } = (0, _testidGenerator.default)();
  const isEnabledParentalSection = config?.parentalConsent?.isEnabled;
  const correctApprovals = initialData?.legalDetails?.approvals && (0, _utils.applyNegative)(initialData.legalDetails.approvals, Boolean(config?.legalDetails?.approvals?.shared?.displayAsNegative));
  const validationSchema = (0, _validationSchema.useGetSchema)(config);
  const methods = (0, _reactHookForm.useForm)({
    defaultValues: {
      ...initialData,
      legalDetails: {
        ...initialData?.legalDetails,
        approvals: correctApprovals
      },
      contactDetails: {
        ...initialData?.contactDetails,
        contactEmail: {
          value: undefined
        },
        contactTelephone: {
          value: undefined
        }
      }
    },
    reValidateMode: 'onChange',
    mode: 'all',
    resolver: (0, _zod.zodResolver)(validationSchema)
  });
  const {
    formState,
    watch,
    resetField
  } = methods;
  const {
    isSubmitting,
    isValid
  } = formState;
  const formValues = watch();
  const {
    errorData
  } = (0, _contexts.useFormError)();
  const {
    customErrors
  } = errorData;
  const invalidCodeErrors = customErrors?.filter(er => !checkNestedKey(formValues, er.fieldName));
  const isClearCachePassword = customErrors?.length && config?.identityDetails?.password?.isAvailable;
  (0, _react.useEffect)(() => {
    isParentalConsent(Boolean(formValues.parentalConsent?.email?.value ?? formValues.parentalConsent?.telephone?.value));
  }, [formValues, isParentalConsent]);
  (0, _react.useEffect)(() => {
    if (isClearCachePassword && !awaitingRes) {
      resetField('identityDetails.password.value', {
        defaultValue: ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps --- not needed other property
  }, [awaitingRes]);
  const isSponsorNumber = initialData?.sponsorDetails?.sponsorNumber?.value;
  const isVisibleSponsorBox = isSponsorNumber && config?.sponsorDetails?.sponsorNumber?.isAvailable;
  const handleFoundSponsor = async () => {
    if (formValues.sponsorDetails?.sponsorNumber?.value) {
      onSubmitForm({
        sponsorDetails: formValues.sponsorDetails,
        customerId: formValues.customerId,
        registrationState: initialData?.registrationState ?? 'Pending',
        registrationType: formValues.registrationType,
        registrationId: formValues.registrationId
      }, true);
    }
    setFocusSponsor(false);
  };
  const handleFindSponsorByInvitationCode = async () => {
    if (formValues.sponsorDetails?.invitationCode?.value) {
      onSubmitForm({
        sponsorDetails: formValues.sponsorDetails,
        customerId: formValues.customerId,
        registrationState: initialData?.registrationState ?? 'Pending',
        registrationType: formValues.registrationType,
        registrationId: formValues.registrationId
      }, true);
    }
    setFocusSponsor(false);
  };
  (0, _react.useEffect)(() => {
    if ((customerReferral?.ConsultantNumber ?? customerReferral?.InternationalPbsCode) && customerReferral.ReferralType) {
      onSubmitForm({
        customerId: formValues.customerId,
        registrationState: initialData?.registrationState ?? 'Pending',
        registrationType: formValues.registrationType,
        registrationId: formValues.registrationId
      }, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps --- we need only on mount
  }, []);
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_reactHookForm.FormProvider, {
    ...methods,
    children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_components2.FormValidationSummary, {
      invalidPropertyErrors: invalidCodeErrors,
      customerServiceUrl: supportCenterUrl,
      loginUrl: loginUrl
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(StyledForm, {
      noValidate: true,
      sx: {
        marginTop: awaitingRes ? '5px' : 0
      },
      onSubmit: methods.handleSubmit(data => onSubmitForm(data)),
      children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_mui.Grid, {
        container: true,
        justifyContent: "center",
        order: 1,
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_commonFormFields.TextFormFieldByConfig, {
          name: "personalDetails.firstName.value",
          label: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_FirstName,
            id: _models.TranslationKey.PersonalDetails_FirstName
          }),
          ...config?.personalDetails?.firstName,
          isMandatory: config?.personalDetails?.firstName?.validation?.isMandatory,
          useUpperCase: config?.personalDetails?.useUpperCase?.isEnabled,
          customErrors: customErrors
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_commonFormFields.TextFormFieldByConfig, {
          name: "personalDetails.lastName.value",
          label: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_LastName,
            id: _models.TranslationKey.PersonalDetails_LastName
          }),
          ...config?.personalDetails?.lastName,
          isMandatory: config?.personalDetails?.lastName?.validation?.isMandatory,
          useUpperCase: config?.personalDetails?.useUpperCase?.isEnabled,
          customErrors: customErrors
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_commonFormFields.TextFormFieldByConfig, {
          enableLowercaseLetters: true,
          name: "contactDetails.contactEmail.value",
          label: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_Email,
            id: _models.TranslationKey.PersonalDetails_Email
          }),
          helperText: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_Email_Tooltip,
            id: _models.TranslationKey.PersonalDetails_Email_Tooltip
          }),
          customPlaceholder: formatMessage(_models.TranslationKey.PersonalDetails_EmailPlaceholder),
          keyboardType: "email",
          ...config?.contactDetails?.email,
          isMandatory: config?.contactDetails?.email?.validation?.isMandatory,
          customErrors: customErrors
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_commonFormFields.PhoneFieldByConfig, {
          name: "contactDetails.contactTelephone.value",
          label: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_Phone,
            id: _models.TranslationKey.PersonalDetails_Phone
          }),
          labelWithPrefix: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_PhoneWithPrefix,
            id: _models.TranslationKey.PersonalDetails_PhoneWithPrefix
          }),
          helperText: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            id: _models.TranslationKey.PersonalDetails_TooltipPhone,
            defaultMessage: _constants.defaultTranslations.PersonalDetails_TooltipPhone
          }),
          helperTextWithPrefix: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            id: _models.TranslationKey.PersonalDetails_TooltipPhoneWithPrefix,
            defaultMessage: _constants.defaultTranslations.PersonalDetails_TooltipPhoneWithPrefix
          }),
          customPlaceholder: formatMessage(_models.TranslationKey.PersonalDetails_PhonePlaceholder),
          customPlaceholderWithPrefix: formatMessage(_models.TranslationKey.PersonalDetails_PlaceholderPhoneWithPrefix),
          initialValue: formValues.contactDetails?.contactTelephone?.value,
          ...config?.contactDetails?.telephone,
          isMandatory: config?.contactDetails?.telephone?.validation?.isMandatory,
          customErrors: customErrors
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_commonFormFields.PasswordFieldByConfig, {
          label: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_Password,
            id: _models.TranslationKey.PersonalDetails_Password
          }),
          name: "identityDetails.password.value",
          isSubmitting: isSubmitting,
          minLength: config?.identityDetails?.password?.validation?.minLength,
          maxLength: config?.identityDetails?.password?.validation?.maxLength,
          isMandatory: config?.identityDetails?.password?.validation?.isMandatory,
          customErrors: customErrors,
          ...config?.identityDetails?.password
        }), config?.address?.operations?.read?.isEnabled ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_sections.AddressSection, {
          configAddress: config.address,
          actualValues: formValues,
          useUpperCaseAddress: config.personalDetails?.useUpperCase?.isEnabled
        }) : null, /*#__PURE__*/(0, _jsxRuntime.jsx)(_commonFormFields.TextFormFieldByConfig, {
          name: "personalDetails.uniqueId.value",
          label: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_UniqueId,
            id: _models.TranslationKey.PersonalDetails_UniqueId
          }),
          helperText: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_UniqueId_Tooltip,
            id: _models.TranslationKey.PersonalDetails_UniqueId_Tooltip
          }),
          ...config?.personalDetails?.uniqueId,
          isMandatory: config?.personalDetails?.uniqueId?.validation?.isMandatory,
          customErrors: customErrors
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_commonFormFields.TextFormFieldByConfig, {
          name: "personalDetails.passport.value",
          label: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_Passport,
            id: _models.TranslationKey.PersonalDetails_Passport
          }),
          ...config?.personalDetails?.passport,
          isMandatory: config?.personalDetails?.passport?.validation?.isMandatory,
          customErrors: customErrors
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_commonFormFields.TextFormFieldByConfig, {
          name: "personalDetails.taxCode.value",
          label: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_TaxCode,
            id: _models.TranslationKey.PersonalDetails_TaxCode
          }),
          ...config?.personalDetails?.taxCode,
          isMandatory: config?.personalDetails?.taxCode?.validation?.isMandatory,
          customErrors: customErrors
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_commonFormFields.TextFormFieldByConfig, {
          name: "personalDetails.taxCode2.value",
          label: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_TaxCode2,
            id: _models.TranslationKey.PersonalDetails_TaxCode2
          }),
          ...config?.personalDetails?.taxCode2,
          isMandatory: config?.personalDetails?.taxCode2?.validation?.isMandatory,
          customErrors: customErrors
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_commonFormFields.SelectFormFieldByConfig, {
          name: "personalDetails.gender.value",
          label: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_Gender,
            id: _models.TranslationKey.PersonalDetails_Gender
          }),
          labelMapper: _Localization.GenderLabelMapper,
          placeholder: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_Gender_Placeholder,
            id: _models.TranslationKey.PersonalDetails_Gender_Placeholder
          }),
          ...config?.personalDetails?.gender,
          isMandatory: config?.personalDetails?.gender?.validation?.isMandatory,
          customErrors: customErrors
        }), isVisibleSponsorBox ? null : /*#__PURE__*/(0, _jsxRuntime.jsx)(_components2.SponsorFieldByConfig, {
          name: "sponsorDetails.sponsorNumber.value",
          label: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_Sponsor,
            id: _models.TranslationKey.PersonalDetails_Sponsor
          }),
          helperText: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_Sponsor_Tooltip,
            id: _models.TranslationKey.PersonalDetails_Sponsor_Tooltip
          }),
          handleOnBlur: async () => handleFoundSponsor(),
          handleOnFocus: () => setFocusSponsor(true),
          ...config?.sponsorDetails?.sponsorNumber
        }), isVisibleSponsorBox ? null : /*#__PURE__*/(0, _jsxRuntime.jsx)(_components2.SponsorFieldByConfig, {
          name: "sponsorDetails.invitationCode.value",
          label: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_Invite_Code,
            id: _models.TranslationKey.PersonalDetails_Invite_Code
          }),
          helperText: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_Invite_Code_Helper_Text,
            id: _models.TranslationKey.PersonalDetails_Invite_Code_Helper_Text
          }),
          allowOnlyNumbers: false,
          handleOnBlur: async () => handleFindSponsorByInvitationCode(),
          handleOnFocus: () => setFocusSponsor(true),
          ...config?.sponsorDetails?.invitationCode
        }), isVisibleSponsorBox && initialData.registrationId ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_components2.SponsorBox, {
          title: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_SponsorTitle,
            id: _models.TranslationKey.PersonalDetails_SponsorTitle
          }),
          registrationId: initialData.registrationId,
          refreshMetadata: refreshMetadata,
          displayOrder: config.sponsorDetails?.sponsorNumber?.displayOrder,
          invitationCode: formValues.sponsorDetails?.invitationCode?.value
        }) : null, /*#__PURE__*/(0, _jsxRuntime.jsx)(_commonFormFields.DatePickerField, {
          name: "personalDetails.birthday.value",
          label: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_DateOfBirth,
            id: _models.TranslationKey.PersonalDetails_DateOfBirth
          }),
          ...config?.personalDetails?.birthDate,
          isMandatory: config?.personalDetails?.birthDate?.validation?.isMandatory,
          minDate: config?.personalDetails?.birthDate?.validation?.minValue,
          maxDate: config?.personalDetails?.birthDate?.validation?.maxValue,
          customErrors: customErrors
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_sections.LegalSection, {
          config: config?.legalDetails,
          registrationCustomerId: initialData?.customerId
        }), isEnabledParentalSection ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_components2.ParentalConsent, {
          config: config.parentalConsent
        }) : null, /*#__PURE__*/(0, _jsxRuntime.jsx)(SubmitButtton, {
          variant: "contained",
          color: "primary",
          type: "submit",
          disabled: isSubmitting || !isValid || focusSponsor,
          ...getTestIdProps('submit-btn-personalForm'),
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Localization.Localization, {
            defaultMessage: _constants.defaultTranslations.PersonalDetails_Button_Continue,
            id: _models.TranslationKey.PersonalDetails_Button_Continue
          })
        })]
      })
    })]
  });
};
exports.RegisterForm = RegisterForm;
//# sourceMappingURL=RegisterForm.js.map